"use strict";

/**
 * Reads everything the record sheet shows, from existing endpoints only:
 *   CisInspectionsQuery   the register, "latest" view (amendments applied)
 *   CisPhotosList         photo metadata of the capture
 *   CisPhotoThumbnail     one thumbnail per photo
 *   CisAmendmentsList     the change history of the capture
 *   CisNcrsQuery          the NCR raised from this record
 *
 * The register is the one read the sheet cannot do without: if it fails, or
 * does not hold the record, the result is { ok: false, errorCode } and the
 * sheet shows an error with Retry, never an empty record. A failed photos,
 * history or NCR read is reported on its own so the sheet can say which part
 * is missing. `api` is the client from createApiClient; `makeUrl(blob)` turns
 * a blob into a URL the caller revokes when the sheet closes.
 */

import { findNcrForInspection, findRegisterRowByInspectionNumber } from "./shape.js";

const okArray = (result) => Boolean(result && result.ok && Array.isArray(result.body));

export async function loadRecord({ api, identity, makeUrl }) {
  const { code, inspectionNumber } = identity;
  const [registerResult, ncrResult] = await Promise.all([api.fetchInspectionsRange(code), api.fetchNcrs(code)]);
  if (!okArray(registerResult)) {
    const status = registerResult && registerResult.status;
    return { ok: false, errorCode: status === 401 || status === 403 ? "not_allowed" : "register_unavailable" };
  }
  const row = findRegisterRowByInspectionNumber(registerResult.body, inspectionNumber);
  if (!row) return { ok: false, errorCode: "not_found" };

  const ncrRow = okArray(ncrResult) ? findNcrForInspection(ncrResult.body, inspectionNumber) : null;
  const ncr = ncrRow ? { code, ncrNumber: ncrRow.ncrNumber, title: ncrRow.title || "" } : null;

  let photos = [];
  let photosError = false;
  let amendments = [];
  let amendmentsError = false;
  if (row.clientCaptureId) {
    const [photosResult, amendmentsResult] = await Promise.all([
      api.fetchCapturePhotos(code, row.clientCaptureId),
      api.fetchCaptureAmendments(code, row.clientCaptureId),
    ]);
    if (okArray(photosResult)) {
      photos = await Promise.all(
        photosResult.body.map(async (p) => {
          const blob = await api.fetchPhotoThumbnailBlob(code, p.clientPhotoId, p.driveItemId);
          const url = blob && blob.ok && blob.blob ? makeUrl(blob.blob) : "";
          return {
            key: p.clientPhotoId,
            photoId: p.clientPhotoId,
            driveItemId: p.driveItemId || "",
            caption: p.caption || "",
            capturedAtUtc: p.capturedAtUtc || "",
            url,
          };
        })
      );
    } else {
      photosError = true;
    }
    if (okArray(amendmentsResult)) amendments = amendmentsResult.body;
    else amendmentsError = true;
  }

  return {
    ok: true,
    row,
    rows: registerResult.body,
    photos,
    photosError,
    amendments,
    amendmentsError,
    ncr,
    ncrError: !okArray(ncrResult),
  };
}

/** One photo at full size: { ok, url } (the caller revokes the url when the sheet closes). */
export async function loadFullPhoto({ api, code, photo, makeUrl }) {
  const result = await api.fetchPhotoContentBlob(code, photo.photoId, photo.driveItemId);
  if (!result || !result.ok || !result.blob) return { ok: false, url: "" };
  return { ok: true, url: makeUrl(result.blob) };
}
